import { useEffect, useRef } from 'react'
import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { queryCache } from '@/lib/cache'

interface UseRealtimeOptions {
  event?: 'INSERT' | 'UPDATE' | 'DELETE' | '*'
  schema?: string
  filter?: string
  onInsert?: (payload: any) => void
  onUpdate?: (payload: any) => void
  onDelete?: (payload: any) => void
  onChange?: (payload: any) => void
}

/**
 * Hook for Supabase Realtime subscriptions
 */
export function useRealtime(
  table: string,
  options: UseRealtimeOptions = {}
) {
  const channelRef = useRef<RealtimeChannel | null>(null)

  useEffect(() => {
    const {
      event = '*',
      schema = 'public',
      filter,
      onInsert,
      onUpdate,
      onDelete,
      onChange
    } = options

    // Create unique channel name
    const channelName = `${table}-${Date.now()}`

    // Create channel
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes' as any,
        {
          event,
          schema,
          table,
          filter
        },
        (payload: RealtimePostgresChangesPayload<any>) => {
          console.log('Realtime event:', payload.eventType, payload)

          // Invalidate relevant cache entries
          queryCache.invalidatePattern(table)

          // Call appropriate handler
          switch (payload.eventType) {
            case 'INSERT':
              onInsert?.(payload)
              break
            case 'UPDATE':
              onUpdate?.(payload)
              break
            case 'DELETE':
              onDelete?.(payload)
              break
          }

          // Always call onChange if provided
          onChange?.(payload)
        }
      )
      .subscribe((status) => {
        console.log(`Realtime subscription ${channelName}:`, status)
      })

    channelRef.current = channel

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
      }
    }
  }, [table, JSON.stringify(options)])

  return channelRef.current
}

/**
 * Hook for knowledge table realtime updates
 */
export function useKnowledgeRealtime(
  onUpdate?: (data: any) => void
) {
  return useRealtime('knowledge', {
    event: '*',
    onChange: (payload) => {
      console.log('Knowledge table changed:', payload)
      onUpdate?.(payload)
    }
  })
}

/**
 * Hook for import_sources table realtime updates
 */
export function useImportSourcesRealtime(
  onUpdate?: (data: any) => void
) {
  return useRealtime('import_sources', {
    event: '*',
    onChange: (payload) => {
      console.log('Import sources changed:', payload)
      onUpdate?.(payload)
    }
  })
}